import { Avatar, Box, HStack, Icon, Text, VStack } from "@chakra-ui/react";
import { ChevronRightIcon } from "@heroicons/react/24/outline";
import { signInWithCustomToken } from "firebase/auth";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { auth } from "../../auth/config";
import { AppContainer } from "../../components/app-container";
import { ContainerLoading } from "../../components/container-loading";
import {
  useProfileLoginMutation,
  useProfilesQuery,
} from "../../graphql/generated/user-hooks";
import { Routes } from "../../helpers/router";
import { getTenantConfig } from "../../helpers/get-tenant-config";

export const Profiles = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [profileLogginIn, setProfileLogginIn] = useState(false);
  const profileId = searchParams.get("profile_id");
  const token = searchParams.get("token");

  const { data, loading } = useProfilesQuery({
    fetchPolicy: "network-only",
    context: {
      endpoint: "fitnext",
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    },
    skip: Boolean(profileId),
  });

  const profiles = data?.profiles.edges ?? [];
  const validProfiles =
    profiles.filter((node) => isValidTenant(node.node.tenantId)) ?? [];

  const [profileLogin] = useProfileLoginMutation({
    context: {
      endpoint: "fitnext",
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    },
    onCompleted: (data) => {
      setProfileLogginIn(true);
      signInWithCustomToken(auth, data.token).then(() =>
        navigate(Routes.Dashboard())
      );
    },
  });

  useEffect(() => {
    if (validProfiles.length > 0) {
      if (profileId) {
        const profile = validProfiles.find(
          (profile) => profile.node.id === profileId
        );
        if (profile) {
          profileLogin({ variables: { id: profileId } });
        }
      } else {
        if (validProfiles.length === 1) {
          const profile = validProfiles[0];
          profileLogin({ variables: { id: profile.node.id } });
        }
      }
    }
  }, [data]);

  if (
    loading ||
    !data ||
    profileLogginIn ||
    profileId ||
    validProfiles.length === 1
  ) {
    return (
      <AppContainer bg="slate.50">
        <ContainerLoading />
      </AppContainer>
    );
  }

  return (
    <AppContainer bg="slate.50">
      <VStack w="full" flex={1} justify="center" spacing={0}>
        <VStack w="full" maxW="440px" spacing={0} px={4}>
          <Text
            fontFamily="'Barlow Condensed', sans-serif"
            fontSize="28px"
            fontWeight={700}
            color="#13192E"
            textAlign="center"
            mb="6px"
          >
            {t("profiles.title")}
          </Text>
          <Text
            fontSize="16px"
            color="slate.500"
            textAlign="center"
            mb={7}
          >
            {t("profiles.subtitle")}
          </Text>

          <VStack w="full" spacing={3}>
            {profiles.length === 0 ? (
              <Text fontSize="16px" color="slate.500">
                {t("profiles.noProfiles")}
              </Text>
            ) : profiles.map(({ node }) => {
              const valid = isValidTenant(node.tenantId);
              return (
                <HStack
                  role="button"
                  w="full"
                  key={node.id}
                  cursor={valid ? "pointer" : "not-allowed"}
                  px={5}
                  py={4}
                  bg={valid ? "white" : "slate.100"}
                  borderWidth="1.5px"
                  borderColor={valid ? "slate.200" : "slate.200"}
                  borderRadius="12px"
                  opacity={valid ? 1 : 0.6}
                  _hover={valid ? { borderColor: "slate.300", bg: "slate.50" } : {}}
                  transition="all 0.15s"
                  onClick={
                    !valid
                      ? undefined
                      : () => profileLogin({ variables: { id: node.id } })
                  }
                >
                  <Avatar
                    size="md"
                    name={node.firstName}
                    src={node.avatarUrl ?? undefined}
                    mr={3}
                  />
                  <VStack align="start" spacing={0} flex={1}>
                    <Text fontSize="16px" fontWeight={600} color="#13192E">
                      {node.firstName} {node.lastName}
                    </Text>
                    {!valid && (
                      <Text fontSize="14px" color="slate.500">
                        {t("profiles.invalidProfile")}
                      </Text>
                    )}
                  </VStack>
                  {valid && (
                    <Icon boxSize={5} color="slate.400">
                      <ChevronRightIcon />
                    </Icon>
                  )}
                </HStack>
              );
            })}
          </VStack>
        </VStack>
      </VStack>
    </AppContainer>
  );
};

const isValidTenant = (tenantId: string) => getTenantConfig().id === tenantId;
